import React, { useContext, useEffect, useState } from "react";
import { HashRouter, useHistory } from "react-router-dom";
import { useWindowSize } from 'usehooks-ts';

// contexts
import { RunContextProvider } from "./Contexts/RunContext";
import SwapBotContext from "./Contexts/SwapBotContext.js";
import { TxStateContextProvider } from "./Contexts/TxStateContext";
import { GlobalErrorContextProvider } from "./Contexts/GlobalErrorContext";
import {
	get_all_wallet_backups_from_localcache, UserContext, UserContextProvider
} from "./Contexts/UserContext/UserContext.js";

// others
import useLog from "./hooks/useLog.js";
import Constants, { appConfig } from "./config.js";
import IpcLogRenderer from "./helpers/IpcLogRenderer";

// css & images
import "./resources/css/index.css";
import "./resources/css/global.css";
// import 'bootstrap/dist/css/bootstrap.min.css';  // required for styles even after npm install
// robot font for material-ui
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { theme } from "./theme.js";
import { ThemeProvider } from "@mui/material/styles";

import { getLog } from "./helpers/helpers.js"
import AppContent from "./AppContent.js";
import TokkenR_Name from "./components/TokkenR_Name.js";
import LoginLogoutButton from "./components/LoginLogoutButton.js";
import MobileTabBar from "./components/MobileTabBar.js";



// detect if running as web-app or electron-app
let is_web_app = false;
if (typeof window.require === 'function'
	&& typeof window.process === 'object'
	&& window.process.type === 'renderer'
) { // The application is running in Electron
	is_web_app = false
} else { // The application is running in a web browser
	is_web_app = true
}
export { is_web_app }

if (!is_web_app) {
	// redirect the console to file logger through ipc
	console = new IpcLogRenderer()
	console.log('Running as an Electron app');
} else {
	console.log('Running as a web app');
	alertIfFirefox();
}
console.log("app.js: is_web_app =", is_web_app)

function alertIfFirefox() {
	const ua = navigator.userAgent;
	if (ua.indexOf("Firefox") > -1) {
		alert("Attention : Vous utilisez Firefox. Le support de ce navigateur n'est pas encore complet.");
	}
}

// --- custom scrollbars, we inject them into the <head> dynamically
const customScrollbarStyles = `
	::-webkit-scrollbar {
		width: 5px; /* 10px */
	}
	/* default color : track #f1f1f1, handle #888, hover #555 */
	/* Track */
	::-webkit-scrollbar-track {
		background: `+ appConfig.theme.backgroundColorLighter1 + `; 
	}
	/* Handle */
	::-webkit-scrollbar-thumb {
		background: `+ appConfig.theme.textColor2 + `;
		border-radius: 10em;
	}
	/* Handle on hover */
	::-webkit-scrollbar-thumb:hover {
		background: `+ appConfig.theme.primaryColor + `;
	}
`;
// Create a <style> element and set its content to the custom styles
const styleElement = document.createElement('style');
styleElement.innerHTML = customScrollbarStyles;
// Append the <style> element to the document's <head>
document.head.appendChild(styleElement);

// --- end of: custom scrollbars



/*
desktop app: todo:
  bouton Agrandir et Rétrécir la fenetre Electron
  voir : https://stackoverflow.com/a/31174463
  coder les fonctions dans preload.js
  et les utiliser ici
*/
const log = (...args) => {
	console.log("app.js:", ...args)
}

const is_dev_server = !!!window.electron
// log("window.contextMenu=", window.contextMenu); // not used anymore, but if you need it add it to preload
log("is_dev_server=", is_dev_server, "window.electron=", window.electron)

export const logpath = is_dev_server ? "" : window.electron.getLogPath()
log("logpath=", logpath)
log("app version", Constants.APP_VERSION)

// log to file for ui (renderer) side too
// console.log = window.electron.getElectronLog().log  // Error: An object could not be cloned.

// log all logs to a file when in production
log("process.env.NODE_ENV = " + process.env.NODE_ENV)
if (process.env.NODE_ENV === "production") {
	log("\n\n\n\n\n ***** APP STARTING ***** \n\n\n")
}



// remove F5 & ctrl+r (reload page)
const Mousetrap = require("mousetrap");
// map multiple combinations to the same callback
Mousetrap.bind(["command+r", "ctrl+r", "f5"], () => {
	console.log("command+r or control+r or f5");
	// return false to prevent default behavior and stop event from bubbling
	return false;
})



// TODO move that to its own AppLoadContext.js file
// we need this in order to not cause useless re-renders that create infinite loops due to context.isloading
export const AppLoadContext = React.createContext("")

export const AppLoadContextProvider = (props) => {
	const function_id = "AppLoadContextProvider(): "
	const classname = function_id
	const log = useLog(function_id)

	const userContext = useContext(UserContext)
	const swapBotContext = useContext(SwapBotContext)

	// having a local var should help the app react faster to user input
	const [isloading, set_isloading] = useState(true)

	const reload = async () => {
		set_isloading(true)
		await userContext.reload_wallet()
		// await swapBotContext.reload() // TODO reactivate later
	}

	/* TODO reactivate swapBotContext.isloading here
		but this made the wallet stuck in reload mode 
		To debug this search the logs for "AppLoadContextProvider" and check */
	const wallet_is_loading = userContext.isloading  // || swapBotContext.isloading
	if (wallet_is_loading != isloading) set_isloading(wallet_is_loading)

	log("userContext.isloading=", userContext.isloading,
		", swapBotContext.isloading=", swapBotContext.isloading)

	const state = {
		isloading: isloading,
		reload: reload,
	}

	return (
		<AppLoadContext.Provider value={state}>
			{props.children}
		</AppLoadContext.Provider>
	)
}



export const ReloadButton = (props) => {
	const appLoadContext = useContext(AppLoadContext)
	const log = useLog("ReloadButton")

	const isloading = appLoadContext.isloading
	log("RELOAD BUTTON isloading :", isloading)
	return (
		<div className={props.className} style={props.style} onClick={appLoadContext.reload}>
			<i className={
				"reloadIcon fas fa-sync " + (isloading ? "fa-spin" : "")
			} />
		</div>
	)
}



export const AppContext = (props) => {
	return (
		<GlobalErrorContextProvider console={console}>
			<RunContextProvider>
				{/* <RuncraftContextProvider> */}
				<TxStateContextProvider>
					<UserContextProvider>
						{/* <SwapBotContextProvider> */}
						<AppLoadContextProvider>
							<HashRouter>
								{props.children}
							</HashRouter>
						</AppLoadContextProvider>
						{/* </SwapBotContextProvider> */}
					</UserContextProvider>
				</TxStateContextProvider>
				{/* </RuncraftContextProvider> */}
			</RunContextProvider>
		</GlobalErrorContextProvider>
	)
}



export const route_restore_wallet_from_file = "/restore_wallet_from_file"
export const route_wallet_unlock = "/wallet_unlock"

/**
 * if we have some wallet backups we redirect
 * to load them directly to save the user 2 clicks
 */
export const AutoRedirecter = (props) => {
	const function_id = "AutoRedirecter: "
	const log = getLog(function_id)

	const [wallet_backups, set_wallet_backups] = useState(null)
	const history = useHistory()
	if (!wallet_backups) {
		const new_wallet_backups = get_all_wallet_backups_from_localcache()
		set_wallet_backups(new_wallet_backups)
	}
	useEffect(() => {
		log("history=", history, "\n\n\n\n")
		if (history) {
			if (wallet_backups && wallet_backups.length > 0) {
				history.push(route_restore_wallet_from_file)
			}
		}
	}, [history])
	return null
}


/**
 * LoginLogoutButton now lives in src/components/LoginLogoutButton.js
 * so it can be shared between the desktop top bar and the mobile menu.
 */


const App = (props) => {
	const function_id = "App: ";
	const log = getLog(function_id);

	// see https://stackoverflow.com/a/57049268
	function isElectron() {
		var userAgent = navigator.userAgent.toLowerCase();
		if (userAgent.indexOf(" electron/") > -1) {
			// Electron-specific code
			return true;
		}
		return false;
	}
	console.log("isElectron? " + isElectron())
	console.log("is_web_app=", is_web_app)

	function onClickCloseButton() {
		const function_id_2 = "onClickCloseButton(): ";
		const log2 = (...args) => { log(function_id_2, ...args); };
		log2("clicked, !!window.electron=", !!window.electron);
		if (!window.electron) {
			log2("aborting because !!window.electron=", !!window.electron);
			return;
		}
		window.electron.closeApp();
	}
	function onClickMinimizeButton() {
		const function_id_2 = "onClickMinimizeButton(): ";
		const log2 = (...args) => { log(function_id_2, ...args); };
		log2("clicked, !!window.electron=", !!window.electron);
		if (!window.electron) {
			log2("aborting because !!window.electron=", !!window.electron);
			return;
		}
		window.electron.minimizeApp();
	}
	function onClickUnMaximizeButton() {
		const function_id_2 = "onClickUnMaximizeButton(): ";
		const log2 = (...args) => { log(function_id_2, ...args); };
		log2("clicked, !!window.electron=", !!window.electron);
		if (!window.electron) {
			log2("aborting because !!window.electron=", !!window.electron);
			return;
		}
		window.electron.unMaximizeApp();
	}

	const backgroundColor = appConfig.theme.backgroundColor || "default"

	const root_app_style = {
		backgroundColor: backgroundColor,
		height: "100vh",
	}

	const { width: windowWidth = 0 } = useWindowSize(); // fallback
	const isMobile = windowWidth < Constants.WINDOW_WIDTH_THRESHOLD_PX
	console.log("App.js: isMobile=", isMobile)
	const [isMenuOpen, set_isMenuOpen] = useState(false)
	const onClickMenu = () => { set_isMenuOpen(!isMenuOpen) }
	const onClickMenuClose = () => { set_isMenuOpen(false) }

	// rename name-logo into TopNavBar and treat it as a component. We need to make it optional and displayed only in the desktop app not in the webapp
	return (
		<ThemeProvider theme={theme} >
			<AppContext>
				<div className="App" style={root_app_style}>
					<div className="name-logo">

						{/* <ReloadButton className="reloadButton" /> */}

						<div className="top-center" style={{
							backgroundColor: appConfig.theme.backgroundColorDarker1,
							display: "flex",
						}}>
							{/* // let's remove that logo because it can confuses people if we are on "bsv" on another token than delphe
								<span><img id="main-logo" src={Constants.IMAGES.delphe_logo_D} alt="logo" /></span>
							*/}
							{/* todo add tokkenr logo if app_name.tolowercase() contains "tokkenr" */}
							{/* {Constants.APP_NAME} */}
							{/* TokkenR */}
						<TokkenR_Name withLogo logoWidth="45px"
							fontWeight="500" fontSize="18px"
							style={{
								padding: "6px", paddingLeft: "20px",
								width: "fit-content", boxShadow: "none",
								background: appConfig.theme.backgroundColorDarker1,
							}}
						/>
						<div style={{
							width: "fit-content", display: "flex",
							marginLeft: 'auto', boxShadow: "none", marginRight: "15px",
							alignItems: 'center', fontSize: "smaller", fontWeight: 400,
							color: "lightgrey", background: appConfig.theme.backgroundColorDarker1,
						}}>
							{!isMobile ? <LoginLogoutButton /> : null}
						</div>
						</div>

						{/*
							The mobile menu is now opened via the floating
							bottom-right FAB (.fab-menu, see App render below).
							The old top-right hamburger has been removed to
							free the top bar for the brand only.
						*/}

						{/* continue the buttons:
							https://stackoverflow.com/questions/31171597/atom-electron-close-the-window-with-javascript
						*/}

						{is_web_app ? null : // no need for minimize buttons on web-app
							<div className="topMenuButtonsContainer">
								<button className="topMenuButton" onClick={onClickMinimizeButton}>
									—
								</button>
								<button className="topMenuButton" onClick={onClickUnMaximizeButton}>
									❐
								</button>
								<button className="topMenuButton closeButton" onClick={onClickCloseButton}>
									✖
								</button>
							</div>
						}

					</div>

					<AutoRedirecter />

					<AppContent
						isMenuOpen={isMenuOpen}
						isMobile={isMobile}
						onClickMenuClose={onClickMenuClose}
					/>
				</div>

				{/* Mobile-only bottom tab bar (replaces the old bottom-right
					floating hamburger). Three tabs: Tokens, Settings, More.
					"More" opens the full menu (MenuPanelOptions full-screen),
					which remains as a fallback for less-important links.
					Hidden while the full menu is open (it covers the screen). */}
				{isMobile && !isMenuOpen ? (
					<MobileTabBar onOpenMenu={onClickMenu} />
				) : null}

				{/* // global tokkenr branding at the bottom left
					// import tokkenr_branding from "./resources/images/TokkenrBranding.png"
					<div style={{position:"fixed",bottom:"0",zoom:"0.5",zIndex:"9",pointerEvents: "none"}}>
						<img src={tokkenr_branding} />
					</div>
				*/}
			</AppContext>
		</ThemeProvider>
	);
};

export default App;
